import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { TextareaControl } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import SidebarDrawer from "../../../components/sidebar-drawer/SidebarDrawer";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	IconPicker,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";
import { toggleButtonPresetStyles } from "../../../styles/styles";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			alignment,

			blockPadding,
			blockMargin,
			separatorColor,
			commonColor,
			margin,
			background,
			gap,
			separatorType,
			typography,
			authorTypography,
			separatorSize,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			enableAuthorIcon,
			enableCommentIcon,
			enableDateIcon,
			enableAuthor,
			enableComment,
			enableDate,
			authorIconColor,
			dateIconColor,
			commentIconColor,
			authorIconSize,
			dateIconSize,
			commentIconSize,
			dateIcon,
			commentIcon,
		},
		setAttributes,
	} = props;

	const separatorOptions = [
		{ label: "Dash", value: "dash" },
		{ label: "Dot", value: "dot" },
		{ label: "Pipe", value: "pipe" },
		{ label: "Slash", value: "slash" },
		{ label: "None", value: "none" },
	];

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<Slider
						onChange={(val: any) =>
							setAttributes({
								gap: val,
							})
						}
						label={__("Gap", "magazine-blocks")}
						units={["px", "em", "%"]}
						min={0}
						max={500}
						value={gap}
						resetAttributeKey="gap"
					/>
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Text Alignment", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="start">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="end">
							<TextAlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
				</Panel>
				<Panel title={__("Author", "magazine-blocks")}>
					<Toggle
						checked={enableAuthor}
						onChange={() =>
							setAttributes({
								enableAuthor: !enableAuthor,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableAuthor && (
						<>
							<Toggle
								checked={enableAuthorIcon}
								onChange={() =>
									setAttributes({
										enableAuthorIcon: !enableAuthorIcon,
									})
								}
								label={__("Icon", "magazine-blocks")}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Date", "magazine-blocks")}>
					<Toggle
						checked={enableDate}
						onChange={() =>
							setAttributes({
								enableDate: !enableDate,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableDate && (
						<>
							<Toggle
								checked={enableDateIcon}
								onChange={() =>
									setAttributes({
										enableDateIcon: !enableDateIcon,
									})
								}
								label={__("Icon", "magazine-blocks")}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Comment", "magazine-blocks")}>
					<Toggle
						checked={enableComment}
						onChange={() =>
							setAttributes({
								enableComment: !enableComment,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableComment && (
						<>
							<Toggle
								checked={enableCommentIcon}
								onChange={() =>
									setAttributes({
										enableCommentIcon: !enableCommentIcon,
									})
								}
								label={__("Icon", "magazine-blocks")}
							/>
						</>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("General", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Common Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: commonColor,
											id: "commonColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={commonColor}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={commonColor}
										onChange={(v) =>
											setAttributes({ commonColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Background Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: background,
											id: "background",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={background}
										resetKey="background"
										onReset={(v) => {
											setAttributes({
												background: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={background}
										onChange={(v) =>
											setAttributes({ background: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={typography}
						onChange={(val) => setAttributes({ typography: val })}
						resetAttributeKey="typography"
					/>
				</Panel>
				<Panel title={__("Separator", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: separatorColor,
											id: "separatorColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={separatorColor}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={separatorColor}
										onChange={(v) =>
											setAttributes({ separatorColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Slider
						onChange={(val: any) =>
							setAttributes({
								separatorSize: val,
							})
						}
						label={__("Size", "magazine-blocks")}
						units={["px", "em", "%"]}
						min={0}
						max={500}
						value={separatorSize}
						resetAttributeKey="separatorSize"
					/>
					<Select
						options={separatorOptions}
						onChange={(val) =>
							setAttributes({ separatorType: val })
						}
						value={separatorType}
						label={__("Separator Type", "magazine-blocks")}
					/>
				</Panel>
				{enableAuthor && (
					<>
						<Panel title={__("Author", "magazine-blocks")}>
							<Typography
								value={authorTypography}
								onChange={(val) =>
									setAttributes({ authorTypography: val })
								}
								resetAttributeKey="authorTypography"
							/>
							<PopoverDrawer
								label={__("Icon Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: authorIconColor,
													id: "authorIconColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={authorIconColor}
												resetKey="color"
												onReset={(v) => {
													setAttributes({ color: v });
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={authorIconColor}
												onChange={(v) =>
													setAttributes({
														authorIconColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Slider
								onChange={(val: any) =>
									setAttributes({
										authorIconSize: val,
									})
								}
								label={__("Icon Size", "magazine-blocks")}
								units={["px", "em", "%"]}
								min={0}
								max={500}
								value={authorIconSize}
								resetAttributeKey="authorIconSize"
							/>
						</Panel>
					</>
				)}

				{enableDate && (
					<>
						<Panel title={__("Date", "magazine-blocks")}>
							<IconPicker
								value={dateIcon}
								onChange={(val) =>
									setAttributes({ dateIcon: val })
								}
							/>
							<PopoverDrawer
								label={__("Icon Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: dateIconColor,
													id: "dateIconColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={dateIconColor}
												resetKey="color"
												onReset={(v) => {
													setAttributes({ color: v });
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={dateIconColor}
												onChange={(v) =>
													setAttributes({
														dateIconColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Slider
								onChange={(val: any) =>
									setAttributes({
										dateIconSize: val,
									})
								}
								label={__("Icon Size", "magazine-blocks")}
								units={["px", "em", "%"]}
								min={0}
								max={500}
								value={dateIconSize}
								resetAttributeKey="dateIconSize"
							/>
						</Panel>
					</>
				)}

				{enableComment && (
					<>
						<Panel title={__("Comment", "magazine-blocks")}>
							<IconPicker
								value={commentIcon}
								onChange={(val) =>
									setAttributes({ commentIcon: val })
								}
							/>
							<PopoverDrawer
								label={__("Icon Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: commentIconColor,
													id: "commentIconColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={commentIconColor}
												resetKey="color"
												onReset={(v) => {
													setAttributes({ color: v });
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={commentIconColor}
												onChange={(v) =>
													setAttributes({
														commentIconColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Slider
								onChange={(val: any) =>
									setAttributes({
										commentIconSize: val,
									})
								}
								label={__("Icon Size", "magazine-blocks")}
								units={["px", "em", "%"]}
								min={0}
								max={500}
								value={commentIconSize}
								resetAttributeKey="commentIconSize"
							/>
						</Panel>
					</>
				)}

				<Panel title={__("Spacing", "magazine-blocks")}>
					<Dimensions
						value={margin}
						responsive
						label={__("Margin", "magazine-blocks")}
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ margin: val })}
						type="margin"
						resetAttributeKey="margin"
					/>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
